import { useState } from 'react';
import { capitalize } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Menu from 'components/shared/menu';
import useTruncatedContent from 'hooks/useTruncatedContent';
import { simpleDate } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import CoordinateRequest from 'models/reviews/CoordinateRequest';
import colors from 'styles/theme/colors';
import { LabelBold } from 'styles/typography';
import CancelIcon from 'svg/CancelIcon';
import ThreeDotMenu from 'svg/ThreeDotMenu';

import DismissCoordinateRequestModal from './DismissCoordinateRequestModal';

type Props = {
  review: CoordinateRequest;
};

const NOTES_MAX_HEIGHT = 84;

export default function CoordinateRequestRow({ review }: Props) {
  const navigate = useNavigate();
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showFullNote, setShowFullNote] = useState(false);

  const patient = review.importedPatient;
  const requestedBy = review.requestedByEntityName;
  const providerGroup = review.providerGroup;
  const providerGroupTypeName = providerGroup?.groupType?.displayName;
  const admissionDate = review.data.admissionDate ? simpleDate(review.data.admissionDate) : null;
  const noteText = review.note?.data?.text;
  const {
    containerRef: noteRef,
    needsShowMore,
    truncatedContent,
  } = useTruncatedContent(noteText, NOTES_MAX_HEIGHT, !!noteText);

  const handleComplete = () => {
    if (!patient?.id) return;
    navigate(`/coordinate/${patient.id}`);
  };

  return (
    <Card data-testid={`coordinate-request-row-${review.id}`}>
      <CardBody>
        <Details>
          <PatientNameRow>
            <LabelBold>{patient?.name ?? '—'}</LabelBold>
          </PatientNameRow>
          <MetaRow>
            <Meta>
              <MetaLabel>DOB:</MetaLabel> {patient?.dateOfBirth ? simpleDate(patient.dateOfBirth) : '—'}
            </Meta>
            <Separator>•</Separator>
            <Meta>
              <MetaLabel>Gender:</MetaLabel> {patient?.sex ? capitalize(patient.sex) : '—'}
            </Meta>
            <Separator>•</Separator>
            <Meta>
              <MetaLabel>Requested By:</MetaLabel> {requestedBy || '—'}
            </Meta>
            <Separator>•</Separator>
            <Meta>
              <MetaLabel>Admission Date:</MetaLabel> {admissionDate ?? '—'}
            </Meta>
            {providerGroup?.name && (
              <>
                <Separator>•</Separator>
                <Meta>
                  <MetaLabel>Group:</MetaLabel> {providerGroup.name}
                </Meta>
              </>
            )}
            {providerGroupTypeName && (
              <>
                <Separator>•</Separator>
                <Meta>
                  <MetaLabel>Group Type:</MetaLabel> {providerGroupTypeName}
                </Meta>
              </>
            )}
          </MetaRow>
        </Details>
        <Actions>
          <AccessGuard accessor={PERMISSIONS.reviewCoordinateRequestEdit}>
            <Button variant='outline-primary' size='xsmall' onClick={handleComplete}>
              Complete Request
            </Button>
          </AccessGuard>
          <Menu $size='large'>
            <Menu.Trigger>
              <KebabButton aria-label='Request actions'>
                <ThreeDotMenu />
              </KebabButton>
            </Menu.Trigger>
            <Menu.Content position='right' style={{ marginTop: 4 }}>
              <AccessGuard accessor={PERMISSIONS.reviewCoordinateRequestEdit}>
                <StyledMenuItem onClick={() => setShowDismissModal(true)}>
                  <CancelIcon width={12} height={12} />
                  Dismiss request
                </StyledMenuItem>
              </AccessGuard>
            </Menu.Content>
          </Menu>
        </Actions>
      </CardBody>
      {noteText && (
        <NoteContainer ref={noteRef}>
          <NoteText data-content-text>
            {needsShowMore && !showFullNote ? (
              <>
                {truncatedContent}…{' '}
                <ShowMoreToggle as='span' onClick={() => setShowFullNote(true)}>
                  Show More
                </ShowMoreToggle>
              </>
            ) : (
              noteText
            )}
          </NoteText>
          {needsShowMore && showFullNote && (
            <ShowMoreToggle onClick={() => setShowFullNote(false)}>Show Less</ShowMoreToggle>
          )}
        </NoteContainer>
      )}
      {showDismissModal && <DismissCoordinateRequestModal review={review} onClose={() => setShowDismissModal(false)} />}
    </Card>
  );
}

const Card = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  margin-bottom: 16px;
`;

const CardBody = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 20px;
`;

const Details = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const PatientNameRow = styled.div`
  display: flex;
  align-items: center;
`;

const MetaRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: ${colors.black100};
  font-size: 12px;
  line-height: 18px;
`;

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const MetaLabel = styled.span`
  color: ${colors.black50};
`;

const Separator = styled.span`
  color: ${colors.black25};
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
`;

const KebabButton = styled.button`
  all: unset;
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 16px;
  justify-content: center;
  padding: 0 2px;
`;

const StyledMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const NoteContainer = styled.div`
  border-top: 1px solid ${colors.black10};
  padding: 12px 20px;
`;

const NoteText = styled.p`
  color: ${colors.black100};
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  white-space: pre-wrap;

  &::before {
    content: 'Notes: ';
    font-weight: 500;
  }
`;

const ShowMoreToggle = styled.span`
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 12px;
  line-height: 20px;

  &:hover {
    text-decoration: underline;
  }
`;
